import React from 'react';

import {
  Flexbox,
  FontAwesomeIcon,
  Text,
  Tooltip,
} from '@schoolinks/ui-components';
import cn from 'classnames';

import { bool, func, number, string } from 'prop-types';
import { useLocation } from 'react-router-dom';

import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faShare } from '@fortawesome/pro-regular-svg-icons/faShare';

import ButtonLink from 'components/Common/Buttons/ButtonLink';
import Button from 'components/Common/Buttons/ButtonV3';
import Toggle from 'components/Common/Toggle';

import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import { ensureObject } from 'utils/objects';

import s from './ShowPageHeader.css';

const ShowPageHeader = ({
  credentialName,
  isPublic,
  setIsPublic,
  setCredentialVisibility,
  type,
  credentialId,
  copyToClipboard,
  exportCredential,
  shareCredential,
  hideActions,
}) => {
  const { data: profile } = useFetch({
    apiFunc: Api.studentProfile.getStudentProfile,
    apiFuncName: 'studentProfile.getStudentProfile',
    options: {
      enabled: !isPublic,
    },
  });
  const { isCredentialWalletPublic } = ensureObject(profile);

  const { pathname } = useLocation();
  // Remove the type and credentialId from the pathname
  const credentialsListUrl = pathname.split('/').slice(0, -2).join('/');

  const getTooltip = () => {
    if (!isCredentialWalletPublic) {
      return 'Cannot set credential as public if your Credential Wallet is private.';
    }

    return isPublic
      ? 'Copy public link'
      : 'Cannot copy link to private credential';
  };

  return (
    <Flexbox flexDirection="column">
      <Flexbox
        className={s.header}
        px={2}
        py={1.5}
        flexDirection="column"
        rowGap={1}
      >
        <Flexbox
          className={s.headerWrapper}
          justifyContent="space-between"
          width="100%"
        >
          <Flexbox className={s.third}>
            <ButtonLink
              href={credentialsListUrl}
              color="blue"
              hasIconLeft
              icon="arrow"
              variant="text"
              label="Back"
              aria-label="Back"
            />
          </Flexbox>
          <Flexbox className={s.third} justifyContent="center">
            <Text variant="h4">{credentialName}</Text>
          </Flexbox>
          <Flexbox
            className={cn(s.third, s.buttons)}
            justifyContent="flex-end"
            alignItems="center"
            columnGap={1}
          >
            {!hideActions && (
              <>
                <Toggle
                  labelDirection="left"
                  label="Set as public"
                  checked={isCredentialWalletPublic && isPublic}
                  disabled={!isCredentialWalletPublic}
                  onChange={(_, checked) => {
                    setCredentialVisibility({
                      type,
                      credentialId,
                      data: {
                        isPublic: checked,
                      },
                    });
                    setIsPublic(checked);
                  }}
                />
                <Tooltip title={getTooltip()}>
                  <div>
                    <Button
                      hideLabel
                      variant="filled"
                      hasIconLeft
                      icon={<FontAwesomeIcon icon={faLink} />}
                      onClick={copyToClipboard}
                      disabled={!isPublic || !isCredentialWalletPublic}
                      aria-label="Copy to clipboard"
                    />
                  </div>
                </Tooltip>
                <Tooltip title="Export JSON">
                  <div>
                    <Button
                      variant="filled"
                      hasIconLeft
                      icon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={exportCredential}
                      aria-label="Export credential"
                    />
                  </div>
                </Tooltip>
                <Tooltip title="Store in other wallet">
                  <div>
                    <Button
                      variant="filled"
                      hasIconLeft
                      icon={<FontAwesomeIcon icon={faShare} />}
                      onClick={shareCredential}
                      aria-label="Share credential"
                    />
                  </div>
                </Tooltip>
              </>
            )}
          </Flexbox>
        </Flexbox>
      </Flexbox>
    </Flexbox>
  );
};

ShowPageHeader.propTypes = {
  credentialName: string.isRequired,
  isPublic: bool.isRequired,
  setIsPublic: func.isRequired,
  setCredentialVisibility: func.isRequired,
  type: string.isRequired,
  credentialId: number.isRequired,
  copyToClipboard: func.isRequired,
  exportCredential: func.isRequired,
  shareCredential: func.isRequired,
  hideActions: bool,
};

ShowPageHeader.defaultProps = {
  hideActions: false,
};

export default ShowPageHeader;
